/**
 * NavigationButtons - 底部导航按钮
 * 上一步 / 下一步 / 开始创作
 */
import { motion } from 'framer-motion';
import { useCreationStore } from '../../../store/useCreationStore';
import Button from '../../../components/ui/Button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

import { useNavigate, useSearchParams } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export default function NavigationButtons() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { step, setStep, isStep1Valid, isStep2Valid, isStep3Valid, getCreationData, reset, flow } = useCreationStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveDraft = async (nextStepNum: number): Promise<string | null> => {
    const data = getCreationData();
    const existingStoryId = searchParams.get('storyId');

    const payload = {
      userId: user?.id || '',
      pageCount: data.story.pages,
      targetAge: data.story.age || '5-6',
      theme: data.story.theme,
      artStyle: data.visual.style || 'pixar',
      interests: data.character.traits || [],
      photoUrl: data.character.photoUrl || data.character.photoPreview,
      outline: data.story.outline,
      speed: data.story.speed,
      voiceId: data.visual.voice,
      characterName: data.character.name,
      appearance: data.character.appearance,
      characterType: data.character.characterType || 'human',
      gender: data.character.gender,
      identityCardUrl: data.character.identityCardUrl || undefined,
      storyId: existingStoryId,
      flow,
      supportingCharacters: data.supportingCharacters,
    };

    console.log(`[Create] Auto-save draft at step ${step} → ${nextStepNum}:`, payload);

    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/stories/setup`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || '保存草稿失败');
    }

    // 将 storyId 同步到 URL（如果还没有的话），同时保留 flow 参数防止流程串线
    const returnedStoryId = result.data.storyId;
    const currentFlow = useCreationStore.getState().flow;
    if (returnedStoryId && returnedStoryId !== existingStoryId) {
      navigate(`/create?storyId=${returnedStoryId}&flow=${currentFlow}`, { replace: true });
    }

    return returnedStoryId;
  };

  const handleNext = async () => {
    if (step >= 3) return;

    // step=1 或 step=2 时，先自动保存草稿
    if (step === 1 || step === 2) {
      setIsSubmitting(true);
      try {
        await saveDraft(step + 1);
        setStep((step + 1) as 1 | 2 | 3);
      } catch (error: any) {
        console.error('[Create] Auto-save failed:', error);
        alert('保存草稿失败：' + (error.message || '请检查网络连接'));
        // 保存失败时阻止进入下一步，确保 storyId 正确生成后才能继续
        return;
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setStep((step + 1) as 1 | 2 | 3);
    }
  };

  const handlePrev = async () => {
    if (step <= 1) return;
    // 返回上一步时也自动保存当前步骤的数据
    setIsSubmitting(true);
    try {
      await saveDraft(step - 1);
    } catch (error: any) {
      console.error('[Create] Auto-save on prev failed:', error);
      // 保存失败不阻止返回，因为 localStorage 已有数据
    } finally {
      setIsSubmitting(false);
    }
    setStep((step - 1) as 1 | 2 | 3);
  };

  const handleSubmit = async () => {
    if (!isStep3Valid()) return;

    setIsSubmitting(true);
    const data = getCreationData();

    try {
      // 准备提交数据（字段名与后端对齐）
      const existingStoryId = searchParams.get('storyId');
      const payload = {
        userId: user?.id || '',
        pageCount: data.story.pages,
        targetAge: data.story.age,
        theme: data.story.theme,
        artStyle: data.visual.style,
        interests: data.character.traits,
        photoUrl: data.character.photoUrl || data.character.photoPreview,
        outline: data.story.outline,
        speed: data.story.speed,
        voiceId: data.visual.voice,
        characterName: data.character.name,
        appearance: data.character.appearance,
        characterType: data.character.characterType || 'human',
        gender: data.character.gender,
        storyId: existingStoryId,
        flow,
        supportingCharacters: data.supportingCharacters,
      };
      
      console.log('[Create] Submitting payload:', payload);

      // 调用创建/更新 API
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/stories/setup`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        reset(); // 清空表单
        if (flow === 'diy') {
          navigate(`/create/diy/editor/${result.data.storyId}`);
        } else {
          navigate(`/create/stage/1?storyId=${result.data.storyId}`);
        }
      } else {
        alert((existingStoryId ? '更新' : '创建') + '失败: ' + result.error);
      }
    } catch (error) {
      alert('创建失败，请检查网络连接');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNextDisabled = () => {
    if (step === 1) return !isStep1Valid();
    if (step === 2) return !isStep2Valid();
    return false;
  };

  const getNextTooltip = () => {
    if (step === 1) {
      const data = getCreationData();
      const hasPhoto = !!(data.character.photoUrl || data.character.photoPreview);
      const hasAppearance = data.character.appearance.trim().length > 0;
      if (!data.character.name.trim()) {
        return '请填写角色名称';
      }
      if (data.character.traits.length === 0) {
        return '请选择性格特点';
      }
      if (flow !== 'diy' && !hasPhoto) {
        return '请上传角色照片';
      }
      if (!data.character.gender) {
        return '请先选择性别';
      }
      if (!hasAppearance) {
        return flow === 'diy' ? '请填写角色外貌描述' : '请点击「AI 提取」获取外观描述，确认后可进入下一步';
      }
    }
    if (step === 2) {
      if (!isStep2Valid()) {
        return flow === 'diy' 
          ? '请选择目标年龄和绘本页数'
          : '请选择目标年龄、绘本页数、故事主题，并填写故事梗概';
      }
    }
    return '';
  };

  return (
    <div className="flex items-center justify-between gap-2">
      {/* 上一步按钮（Step 1 时隐藏） */}
      {step > 1 && (
        <Button
          variant="secondary"
          size="lg"
          onClick={handlePrev}
          disabled={isSubmitting}
          leftIcon={<ArrowLeft className="w-5 h-5 shrink-0" />}
          className="whitespace-nowrap shrink-0"
        >
          上一步
        </Button>
      )}

      {/* 步骤指示器（移动端显示） */}
      <div className="flex items-center gap-2 lg:hidden shrink-0">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full transition-colors ${
              s === step ? 'bg-indigo-500' : s < step ? 'bg-amber-400' : 'bg-slate-200'
            }`}
          />
        ))}
      </div>

      {/* 下一步 / 开始创作按钮 */}
      {step === 3 ? (
        <motion.div whileHover={!isStep3Valid() ? {} : { scale: 1.02 }} whileTap={!isStep3Valid() ? {} : { scale: 0.98 }}>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={!isStep3Valid() || isSubmitting}
            isLoading={isSubmitting}
            className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white shadow-lg shadow-indigo-500/30 disabled:opacity-90 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
            rightIcon={!isSubmitting && <ArrowRight className="w-5 h-5 shrink-0" />}
          >
            {isSubmitting ? '创建中...' : '下一步'}
          </Button>
        </motion.div>
      ) : (
        <div className="flex flex-col items-end gap-2">
          {isNextDisabled() && getNextTooltip() && (
            <span className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              {getNextTooltip()}
            </span>
          )}
          <Button
            variant="primary"
            size="lg"
            onClick={handleNext}
            disabled={isNextDisabled() || isSubmitting}
            isLoading={isSubmitting}
            className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white shadow-lg shadow-indigo-500/30 disabled:opacity-90 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
            rightIcon={!isSubmitting && <ArrowRight className="w-5 h-5 shrink-0" />}
          >
            {isSubmitting ? '保存中...' : '下一步'}
          </Button>
        </div>
      )}
    </div>
  );
}
